import React, { useState } from 'react';
import { Search, Factory, ShoppingCart, Truck, ChevronRight, AlertCircle } from 'lucide-react';
import Breadcrumbs from '../Navigation/Breadcrumbs';
import RequestForm from './RequestForm';

function SearchAndPurchasePage() {
  const [formData, setFormData] = useState({
    productLink: '',
    comment: '',
    name: '',
    phone: '',
  });
  const [formErrors, setFormErrors] = useState({
    name: '',
    phone: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  const validateForm = () => {
    const errors = {
      name: '',
      phone: '',
    };
    let isValid = true;

    if (!formData.name.trim()) {
      errors.name = 'Пожалуйста, введите имя';
      isValid = false;
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Пожалуйста, введите телефон';
      isValid = false;
    } else if (!/^\+?[0-9\s-()]+$/.test(formData.phone)) {
      errors.phone = 'Пожалуйста, введите корректный номер телефона';
      isValid = false;
    }

    setFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Here you would typically send the data to your backend
      await new Promise(resolve => setTimeout(resolve, 1000)); // Simulate API call
      setSubmitSuccess(true);
      setFormData({
        productLink: '',
        comment: '',
        name: '',
        phone: '',
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section with Gradient Background */}
      <section className="pt-32 pb-40 px-6 bg-gradient-to-b from-[#1B3BA0] to-[#0F2875] relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1441986300917-64674bd600d8?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-10"></div>
          <div className="absolute inset-0 bg-[#1B3BA0]/50 mix-blend-multiply"></div>
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-[#1B3BA0]/20"></div>
        </div>
        <div className="max-w-[980px] mx-auto relative">
          <div className="mb-12">
            <Breadcrumbs />
          </div>
          <div className="max-w-[780px]">
            <h1 className="text-5xl md:text-7xl font-bold mb-8 tracking-tight">
              <span className="text-white block mb-2">Поиск и выкуп товаров</span>
              <span className="bg-clip-text text-transparent bg-gradient-to-r from-white to-[#99BBFF]">
                из Китая
              </span>
            </h1>
            <p className="text-xl md:text-2xl text-white/80 leading-relaxed">
              Профессиональная помощь в поиске товаров и производителей, выкуп с китайских площадок и прямое взаимодействие с фабриками
            </p>
          </div>
        </div>
      </section>

      {/* Features Grid with Elevated Cards */}
      <section className="py-24 px-6 bg-white relative z-10 -mt-20">
        <div className="max-w-[980px] mx-auto">
          <div className="bg-white rounded-2xl p-8 md:p-12 shadow-2xl border border-gray-100">
            <h2 className="text-3xl font-bold text-[#1B3BA0] mb-6">
              Наши услуги
            </h2>
            <p className="text-lg text-gray-600 mb-12 leading-relaxed">
              Предлагаем полный комплекс услуг по поиску и закупке товаров в Китае для вашего бизнеса.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[
                {
                  icon: <Search className="w-12 h-12 text-[#1B3BA0]" />,
                  title: 'Поиск товаров',
                  description: 'Работаем с площадками 1688, Taobao и другими маркетплейсами Китая'
                },
                {
                  icon: <Factory className="w-12 h-12 text-[#1B3BA0]" />,
                  title: 'Прямой контакт',
                  description: 'Находим производителей напрямую и ведём переговоры от вашего имени'
                },
                {
                  icon: <ShoppingCart className="w-12 h-12 text-[#1B3BA0]" />,
                  title: 'Полное сопровождение',
                  description: 'Организуем выкуп, проверку качества и доставку товара'
                }
              ].map((feature, index) => (
                <div key={index} className="bg-[#1B3BA0]/5 rounded-2xl p-8 hover:bg-[#1B3BA0]/10 transition-all duration-300 transform hover:-translate-y-1">
                  <div className="mb-6">{feature.icon}</div>
                  <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Process Steps with Gradient Background */}
      <section className="py-32 px-6 bg-gradient-to-b from-white to-[#1B3BA0]/5">
        <div className="max-w-[980px] mx-auto">
          <h2 className="text-4xl font-bold text-center text-[#1B3BA0] mb-16">
            Как мы работаем
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Заявка',
                description: 'Вы оставляете заявку с описанием нужного товара или ссылкой на аналог'
              },
              {
                step: '02',
                title: 'Поиск и согласование',
                description: 'Мы находим поставщиков, сравниваем цены и условия, согласовываем с вами'
              },
              {
                step: '03',
                title: 'Выкуп и доставка',
                description: 'Организуем выкуп товара, проверку качества и доставку до вашего склада'
              }
            ].map((phase, index) => (
              <div key={index} className="bg-white rounded-2xl p-8 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="text-6xl font-bold text-[#1B3BA0]/10 mb-6">{phase.step}</div>
                <h3 className="text-xl font-bold text-[#1B3BA0] mb-4">{phase.title}</h3>
                <p className="text-gray-600">{phase.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Form Section */}
      <section className="py-32 px-6 bg-[#1B3BA0]">
        <div className="max-w-[780px] mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Получить расчёт стоимости
          </h2>
          <p className="text-xl text-white/80 mb-12 max-w-2xl mx-auto">
            Заполните форму, и мы свяжемся с вами для обсуждения деталей
          </p>
          <RequestForm source="Поиск и выкуп товаров" />
        </div>
      </section>
    </div>
  );
}

export default SearchAndPurchasePage;